import { useEffect, useMemo, useState } from 'react';
import {
  fetchPastWeather,
  fetchSevenDayForecast,
  geocodeZip,
  type ForecastDay,
} from './weather';

// Shared weather hook for components that want a date→ForecastDay lookup.
// Combines:
//   - up to 90 days of PAST weather (heatmap / "what was the weather on
//     that day?" displays — capacity Week At A Glance)
//   - the next 7 days of FORECAST weather (planning surfaces — capacity
//     7-day forecast tile)
//
// Both come from the same Open-Meteo endpoint via the lib helpers
// (`fetchPastWeather` and `fetchSevenDayForecast`), which already cache
// per-location for 1 hour in localStorage. This hook only adds the
// state-management + zip→geo resolution wrapper so React components
// don't each have to wire up the same useEffect.
//
// Renders are gracefully degraded: if the user has no zip (profile not
// filled in) OR the geocode misses OR the API fails, both arrays come
// back empty and `byDate` is `{}`. Callers should render their UI in a
// "weather unknown" state rather than throwing.

const PAST_WINDOW_DAYS = 90;

export function useWeatherByDate(zip: string | null | undefined) {
  const [past, setPast] = useState<ForecastDay[]>([]);
  const [forecast, setForecast] = useState<ForecastDay[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!zip) {
      setPast([]);
      setForecast([]);
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      const geo = await geocodeZip(zip);
      if (!alive) return;
      if (!geo) {
        setPast([]);
        setForecast([]);
        setLoading(false);
        return;
      }
      const [p, f] = await Promise.all([
        fetchPastWeather(geo, PAST_WINDOW_DAYS),
        fetchSevenDayForecast(geo),
      ]);
      if (!alive) return;
      setPast(p ?? []);
      setForecast(f ?? []);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [zip]);

  // Forecast wins over past on overlapping dates (today appears in both
  // endpoints; the forecast value is the live-updated one).
  const byDate = useMemo(() => {
    const m: Record<string, ForecastDay> = {};
    for (const d of past) m[d.date] = d;
    for (const d of forecast) m[d.date] = d;
    return m;
  }, [past, forecast]);

  return { byDate, past, forecast, loading };
}

/** Local-timezone YYYY-MM-DD string for a Date or ms timestamp. Matches
 *  the date format Open-Meteo returns when `timezone=auto` so callers
 *  can look up weather entries by `day_ms` from the backend without
 *  drifting across timezones. */
export function toLocalISODate(value: number | Date): string {
  const d = typeof value === 'number' ? new Date(value) : value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
